import * as React from 'react'
import { Info, TriangleAlert } from 'lucide-react'
import { cn } from '@/lib/utils'

/*
 * Notice: an inline, persistent message that explains or warns about the page
 * it sits on. The title states the message in words, so tone is never the only
 * carrier of meaning. Set `live` when the notice reports the result of an
 * action, so it is announced politely.
 */
const tones = {
  info: { surface: 'bg-info-surface', text: 'text-info-text', icon: <Info className="size-5" /> },
  warning: { surface: 'bg-warning-surface', text: 'text-warning-text', icon: <TriangleAlert className="size-5" /> },
} as const

function Notice({
  tone,
  title,
  icon,
  live = false,
  className,
  children,
}: {
  tone: keyof typeof tones
  title: string
  icon?: React.ReactNode
  live?: boolean
  className?: string
  children?: React.ReactNode
}) {
  const style = tones[tone]
  return (
    <div
      role={live ? 'status' : undefined}
      className={cn('flex gap-3 rounded-md border border-line px-4 py-3', style.surface, className)}
    >
      <span aria-hidden="true" className={cn('mt-0.5 shrink-0', style.text)}>
        {icon ?? style.icon}
      </span>
      <div className="flex min-w-0 flex-col gap-1">
        <p className={cn('text-sm font-medium wrap-anywhere', style.text)}>{title}</p>
        {children ? <p className="text-sm text-fg">{children}</p> : null}
      </div>
    </div>
  )
}

export { Notice }
